import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react'
import classNames from 'classnames'

import TmoSvg from '../../tmoUiHelpers/TmoSvg'
import TmoButton from '../../tmoButtons/TmoButton'
import useWindowWidth from '../../tmoHooks/useWindowWidth'
import { SORT_DIRECTION, DIMENSIONS } from '../../tmoUtilities/constants'

import style from './DynamicSort.module.css'

const DynamicSort = forwardRef(
  (
    {
      listItems,
      optionsList,
      onSort,
      renderIcon,
      iconClass,
      className,
      testId,
      isActionIconBtn,
      id
    },
    ref
  ) => {
    const [selectState, setSelectState] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [selectedValue, setSelectedValue] = useState('')
    const [sortDirection, setSortDirection] = useState(SORT_DIRECTION.ASC)
    const node = useRef()
    const listBox = useRef()
    const window = useWindowWidth()

    const handleSorting = (items, sortBy, direction) => {
      if (items && items.length > 1 && sortBy) {
        let list = [...items]
        let sortedList =
          direction === SORT_DIRECTION.ASC
            ? list.sort((a, b) => {
                if (a[sortBy] === null) {
                  return 1
                }
                if (b[sortBy] === null) {
                  return -1
                }
                return a[sortBy].toString().localeCompare(b[sortBy].toString())
              })
            : list.sort((a, b) => {
                if (a[sortBy] === null) {
                  return 1
                }
                if (b[sortBy] === null) {
                  return -1
                }
                return b[sortBy].toString().localeCompare(a[sortBy].toString())
              })
        onSort(sortedList)
      }
    }
    const handleOptionClick = (e) => {
      let key = e?.target ? e?.target?.getAttribute('data-name') : ''
      key && setSelectedValue(key)
      key && handleSorting(listItems, key, sortDirection)
      setSelectedIndex(0)
    }

    const handleClickOutside = (e) => {
      if (node.current.contains(e.target)) {
        return
      }
      setSelectState(false)
    }

    const handleDirectionClick = (direction) => {
      setSortDirection(direction)
      handleSorting(listItems, selectedValue, direction)
    }

    useImperativeHandle(ref, () => ({
      getSortedList(data) {
        handleSorting(data, selectedValue, sortDirection)
      }
    }))

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])
    const handleKeyDown = (event, direction) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const listItems = listBox.current.querySelectorAll('[role="option"]')
        if (listItems.length > 0) {
          let newIndex
          if (event.key === 'ArrowDown') {
            newIndex = Math.min(selectedIndex + 1, listItems.length - 1)
          } else {
            newIndex = Math.max(selectedIndex - 1, 0)
          }
          listItems[newIndex].focus()
          setSelectedIndex(newIndex)
        }
      } else if (event.key === 'Enter') {
        const focusedOption = document.activeElement
        if (focusedOption && focusedOption.getAttribute('role') === 'option') {
          const button = focusedOption.querySelector('button')
          if (button) {
            button.click()
          } else {
            if (direction) {
              handleDirectionClick(direction)
            } else {
              handleOptionClick(event)
            }
          }
        }
      }
    }
    return (
      <div
        className={
          isActionIconBtn ? style.sort_primary_warpper : style.sort_warpper
        }
        ref={node}
      >
        {window <= DIMENSIONS.MOBILE_WIDTH ? (
          <TmoButton
            onClick={() => {
              setSelectState(!selectState)
            }}
            tooltip={window <= DIMENSIONS.MOBILE_WIDTH && 'Sort'}
            icon={<TmoSvg className={style.mobile_icon} svgId='icon-sort' />}
            type='magenta_secondary_icon'
            className={style.mobile_button}
            data-testid={testId}
          />
        ) : (
          <React.Fragment>
            {isActionIconBtn ? (
              <TmoButton
                onClick={() => {
                  setSelectState(!selectState)
                }}
                tooltip={window <= DIMENSIONS.MOBILE_WIDTH && 'Sort'}
                icon={<TmoSvg svgId='icon-sort' className={style.sort_icon} />}
                type={
                  isActionIconBtn
                    ? 'magenta_icon_btn'
                    : 'magenta_secondary_icon'
                }
                className={style.mobile_button}
                data-testid={testId}
                role='combobox'
                aria-expanded={selectState.toString()}
                aria-controls='combobox'
                aria-haspopup='listbox'
                value='Select'
                tabindex='0'
                aria-labelledby={id}
                tabIndex={0}
                onKeyDown={handleKeyDown}
              />
            ) : (
              <button
                className={classNames(style.sort_input, className)}
                onClick={() => {
                  setSelectState(!selectState)
                }}
                role='combobox'
                aria-expanded={selectState.toString()}
                aria-controls='combobox'
                aria-haspopup='listbox'
                value='Select'
                tabindex='0'
                aria-labelledby={id}
                tabIndex={0}
                onKeyDown={handleKeyDown}
              >
                Sort By{' '}
                <span className={classNames(style.icon, iconClass)}>
                  {renderIcon}
                </span>
              </button>
            )}
          </React.Fragment>
        )}
        {selectState && (
          <ul
            className={
              isActionIconBtn ? style.sorting_primary_list : style.sorting_list
            }
            role='listbox'
            id='listbox'
            aria-label='Select Options'
            aria-labelledby={id}
            tabIndex={0}
            ref={listBox}
          >
            <div className={style.category}>Sort by</div>
            {optionsList?.length > 0 &&
              optionsList.map((e, index) => (
                <li
                  className={classNames(
                    style.option_list,
                    e.key === selectedValue && style.selected
                  )}
                  data-name={e.key}
                  data-value={e.value}
                  key={`${e.key}-${index}`}
                  onClick={handleOptionClick}
                  title={e.value}
                  aria-label={e.value}
                  id='listbox'
                  aria-labelledby={id}
                  aria-selected={e.value === selectedValue}
                  tabIndex={index + 2}
                  onKeyDown={handleKeyDown}
                  role='option'
                >
                  {e.value}
                </li>
              ))}
            <div>
              <hr />
              <div className={style.category}>Order by</div>
              <li
                className={classNames(
                  style.option_list,
                  sortDirection === SORT_DIRECTION.ASC && style.selected
                )}
                onClick={() => {
                  handleDirectionClick(SORT_DIRECTION.ASC)
                }}
                id='listbox'
                aria-labelledby={id}
                aria-selected={SORT_DIRECTION.ASC === sortDirection}
                tabIndex={0}
                onKeyDown={(e) => {
                  handleKeyDown(e, SORT_DIRECTION.ASC)
                }}
                role='option'
              >
                Ascending
              </li>
              <li
                className={classNames(
                  style.option_list,
                  sortDirection === SORT_DIRECTION.DESC && style.selected
                )}
                onClick={() => {
                  handleDirectionClick(SORT_DIRECTION.DESC)
                }}
                id='listbox'
                aria-labelledby={id}
                aria-selected={SORT_DIRECTION.DESC === sortDirection}
                tabIndex={0}
                onKeyDown={(e) => {
                  handleKeyDown(e, SORT_DIRECTION.DESC)
                }}
                role='option'
              >
                Descending
              </li>
            </div>
          </ul>
        )}
      </div>
    )
  }
)

export default DynamicSort
